<template>
    <tile class="flex flex-col items-center @[1050px]:items-start">
        <!-- Stupid hacky adjustments to try to align title with something that is not alignable (the chart part of the grid) -->
        <targeting-router-link :to="routeTo" class="text-primary text-lg @[1050px]:ml-[18%] @[1100px]:ml-[20%] @[1300px]:ml-[25%]">
            {{ isCustomer ? 'Kundefordringer' : 'Leverandørgjeld' }}
        </targeting-router-link>
        <ag-charts v-if="data?.length" :options="chartOptions" class="w-full grow"/>
        <div v-else class="grow flex items-center text-lg">
            Ingen åpne poster!
        </div>
    </tile>
</template>

<script setup lang="ts">
import {CUSTOMER, SubAccountRelationType} from '@/subaccount/types/SubAccount'
import Tile from '@/dashboard/Tile.vue'
import {fetchAgeDividedReport} from '@/subaccount/subAccount.service'
import {AgCharts} from 'ag-charts-vue3'
import {computed, nextTick, onMounted, onUnmounted, ref} from 'vue'
import {AgChartOptions, AgColorType, AgDonutSeriesTooltipRendererParams} from 'ag-charts-enterprise'
import {accumulateEntriesSum, AgeSegmentSum} from '@/subaccount/ageDividedSacc.service'
import {isDark} from '@/theme/theme.service'
import {agChartProDark, agChartProLight} from '@/agUtils/agChartsTheme'
import {useEmitter} from '@/services/infrastructure/emitter'
import {DELETE_TRANSACTION, SACC_LINES_UPDATED, TRANSACTION_CREATED, TRANSACTION_UPDATED} from '@/types/transaction/TransactionEvent'
import {ORG_SELECTED} from '@/types/events'
import {formatCurrency} from '@/services/formatter.service'
import { TargetingRouterLink } from '@tritt/multipanel'
import {CUSTOMER_AGE_DIVIDED_REPORT, SUPPLIER_AGE_DIVIDED_REPORT} from '@/subaccount'
import {$dt} from '@primeuix/themes'

const {onEvents, cleanUp} = useEmitter()

const props = defineProps({
    relationType: {
        type: String as () => SubAccountRelationType,
        required: true
    }
})


const data = ref<AgeSegmentSum[] | null>(null)
const isCustomer = computed(() => props.relationType === CUSTOMER)
const routeTo = computed(() => {
    return isCustomer.value ? CUSTOMER_AGE_DIVIDED_REPORT : SUPPLIER_AGE_DIVIDED_REPORT
})
const formattedData = computed(() => {
    return data.value?.map(entry => {
        return {
            label: entry.label === 'Not due' ? 'Ikke forfalt' : entry.label,
            value: isCustomer.value ? entry.value : entry.value * -1 // Negate for suppliers
        }
    })
})

//TODO: Find a way to abort if changing tenant
const fetchData = async () => {
    const allSubAccs = []
    let pageNo = 0
    let totalPages = 1
    while (pageNo < totalPages) {
        const page = await fetchAgeDividedReport(props.relationType, pageNo, 50)
        allSubAccs.push(...page.content.map(el => el.sums))
        totalPages = page.totalPages
        pageNo++
    }

    if (allSubAccs.length === 0) {
        data.value = []
        nextTick(() => {
            data.value = null
        }).then()
    } else {
        data.value = accumulateEntriesSum(allSubAccs)
    }
}

const innerChartAmountDisplay = computed<string>(() => {
    if (!data.value) {
        return '0'
    }
    const total = data.value
        .reduce((acc, entry) => acc + entry.value, 0) * (isCustomer.value ? 1 : -1)
    return formatCurrency(total, 0)
})

const percentOfTotal = (value: number): number => {
    if (!data.value || data.value.length === 0) {
        return 0
    }
    const total = data.value
        .reduce((acc, entry) => acc + entry.value, 0) * (isCustomer.value ? 1 : -1)
    return Math.round((value / total) * 100)
}

const colorPalette = computed<AgColorType[]>(() => {
    return (isCustomer.value ?
            [$dt('green.100').value, $dt('green.200').value, $dt('green.300').value, $dt('green.400').value, $dt('green.500').value, $dt('green.600').value, $dt('green.700').value]
            : [$dt('orange.100').value, $dt('orange.200').value, $dt('orange.300').value, $dt('orange.400').value, $dt('orange.500').value, $dt('orange.600').value, $dt('orange.700').value]
    ) as AgColorType[]
})

const chartOptions = computed<AgChartOptions>(() => {
    return {
        data: formattedData.value,
        series: [
            {
                type: 'donut',
                angleKey: 'value',
                legendItemKey: 'label',
                shadow: {
                    enabled: true,
                    color: 'rgba(0, 0, 0, 0.2)'
                },
                sectorSpacing: 4,
                innerLabels: [
                    {
                        text: 'Sum ' + (isCustomer.value ? 'kundefordringer' : 'leverandørgjeld'),
                        fontSize: 12
                    },
                    {
                        text: innerChartAmountDisplay.value,
                        fontSize: 20,
                        spacing: 12,
                        fontWeight: 'bold'
                    }
                ],
                tooltip: {
                    enabled: true,
                    renderer: (params: AgDonutSeriesTooltipRendererParams<AgeSegmentSum>) => {
                        const title = params.datum.label === 'Ikke forfalt' ? 'Ikke forfalt' : params.datum.label + ' dager'
                        return {
                            title,
                            data: [
                                {
                                    label: '',
                                    value: `${formatCurrency(params.datum.value, 0)} (${percentOfTotal(params.datum.value)}%)`
                                }
                            ]
                        }
                    }

                }
            }
        ],
        legend: {
            position: 'right'
        },
        overlays: {
            noData: {
                text: 'Ingen åpne poster'
            }
        },
        theme: {
            ...(isDark.value ? agChartProDark : agChartProLight),
            palette: {
                fills: colorPalette.value
            }
        },
        minHeight: 100,
        minWidth: 100,
        padding: {
            top: 5,
            right: 0,
            bottom: 0,
            left: 0
        },
        background: {
            visible: false
        }
    }
})


onMounted(() => {
    fetchData()
    onEvents([TRANSACTION_CREATED, TRANSACTION_UPDATED, DELETE_TRANSACTION, ORG_SELECTED, SACC_LINES_UPDATED], fetchData)
})

onUnmounted(cleanUp)
</script>

<style scoped>

</style>